import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Trophy, Handshake, TrendingDown, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface EloPredictorProps {
  playerElo: number;
  opponentElo: number;
  opponentName?: string;
}

interface EloPrediction {
  win_change: number;
  draw_change: number;
  loss_change: number;
}

export const EloPredictor = ({ playerElo, opponentElo, opponentName }: EloPredictorProps) => {
  const [prediction, setPrediction] = useState<EloPrediction | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPrediction = async () => {
      try {
        const { data, error } = await (supabase as any).rpc('predict_elo_changes', {
          _player_elo: playerElo,
          _opponent_elo: opponentElo
        });

        if (error) throw error;
        
        if (data && Array.isArray(data) && (data as any[]).length > 0) {
          setPrediction((data as any[])[0]);
        }
      } catch (error) {
        console.error('Error fetching ELO prediction:', error);
        toast.error('Failed to load ELO prediction');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPrediction();
  }, [playerElo, opponentElo]);

  if (isLoading) {
    return (
      <Card className="bg-card/50 backdrop-blur-sm border-border/50">
        <CardContent className="p-3 flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Loading predictions...</span>
        </CardContent>
      </Card>
    );
  }

  if (!prediction) return null;

  return (
    <Card className="bg-card/50 backdrop-blur-sm border-border/50">
      <CardContent className="p-3 space-y-2">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Expected Rating Changes</span>
          <span className="font-medium">
            vs {opponentName || 'Opponent'} ({opponentElo})
          </span>
        </div>
        
        <div className="grid grid-cols-3 gap-2">
          {/* Win */}
          <div className="flex flex-col items-center gap-1 p-2 rounded-lg bg-green-500/10 border border-green-500/20">
            <Trophy className="w-4 h-4 text-green-500" />
            <span className="text-xs text-muted-foreground">Win</span>
            <span className="font-bold text-green-500">
              {prediction.win_change > 0 ? '+' : ''}{prediction.win_change}
            </span>
          </div>

          {/* Draw */}
          <div className="flex flex-col items-center gap-1 p-2 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
            <Handshake className="w-4 h-4 text-yellow-500" />
            <span className="text-xs text-muted-foreground">Draw</span>
            <span className="font-bold text-yellow-500">
              {prediction.draw_change > 0 ? '+' : ''}{prediction.draw_change}
            </span>
          </div>

          {/* Loss */}
          <div className="flex flex-col items-center gap-1 p-2 rounded-lg bg-red-500/10 border border-red-500/20">
            <TrendingDown className="w-4 h-4 text-red-500" />
            <span className="text-xs text-muted-foreground">Loss</span>
            <span className="font-bold text-red-500">
              {prediction.loss_change > 0 ? '+' : ''}{prediction.loss_change}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
